import { Link } from "react-router-dom";
import { Loader2, Info, ChevronLeft, Check, Clock, X, Ban, CalendarX2, Bookmark, Inbox, Send, UserCheck, UsersRound, Minus, Lock, CircleHelp, ListOrdered } from "lucide-react";

import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { STATUS } from "@/lib/memberDerive";

export function PageHeader({ title, intro, back, children, testid }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between lg:mb-7">
      <div className="min-w-0">
        {back && (
          <Link to={back.to} className="mb-2 inline-flex min-h-[44px] items-center gap-1 text-[14px] font-bold text-v3-magenta hover:underline" data-testid="page-back-link">
            <ChevronLeft className="h-4 w-4" aria-hidden="true" /> {back.label}
          </Link>
        )}
        <h1 className="hyphens-auto break-words text-[26px] font-bold leading-[1.3] text-v3-ink lg:text-[34px] lg:leading-[1.25]" data-testid={testid || "page-title"}>{title}</h1>
        {intro && <p className="mt-[10px] max-w-3xl text-[15px] leading-[1.45] text-v3-muted lg:text-[17px]">{intro}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export function Section({ title, icon: Icon, children, testid, id }) {
  return (
    <section id={id} className="rounded-[12px] border border-v3-line bg-white p-[18px] lg:p-5" data-testid={testid} aria-labelledby={id ? `${id}-title` : undefined}>
      {title && (
        <h2 id={id ? `${id}-title` : undefined} className="mb-3 flex items-center gap-2 text-[17px] font-bold text-v3-ink">
          {Icon && <Icon className="h-5 w-5 text-v3-magenta" aria-hidden="true" />} {title}
        </h2>
      )}
      {children}
    </section>
  );
}

const STATUS_ICON = {
  none: Minus, registered: Check, requested: Clock, waitlisted: ListOrdered, offered: Clock, confirmed: Check, declined: X, withdrawn: X,
  cancelled: CalendarX2, activity_cancelled: CalendarX2, saved: Bookmark, incoming: Inbox,
  outgoing: Send, accepted: UserCheck, member: UsersRound, not_member: Minus, join_pending: Clock, join_declined: X, unknown: CircleHelp,
};
const STATUS_TONE = {
  registered: "border-transparent bg-v3-mint text-v3-green", confirmed: "border-transparent bg-v3-mint text-v3-green",
  accepted: "border-transparent bg-v3-mint text-v3-green", member: "border-transparent bg-v3-mint text-v3-green",
  requested: "border-transparent bg-v3-sand text-v3-amber", outgoing: "border-transparent bg-v3-sand text-v3-amber", waitlisted: "border-transparent bg-v3-sand text-v3-amber",
  offered: "border-transparent bg-v3-sand text-v3-amber", join_pending: "border-transparent bg-v3-sand text-v3-amber",
  join_declined: "border-red-700/30 bg-red-50 text-red-800", declined: "border-red-700/30 bg-red-50 text-red-800",
  incoming: "border-transparent bg-v3-blush text-v3-magenta",
  activity_cancelled: "border-red-700/30 bg-red-50 text-red-800", cancelled: "border-red-700/30 bg-red-50 text-red-800",
};

export function StatusBadge({ status, testid }) {
  const Icon = STATUS_ICON[status] || Minus;
  return (
    <span data-testid={testid} data-status={status}
      className={`inline-flex min-h-[29px] items-center gap-1.5 rounded-full border px-3 py-1 text-[12.5px] font-bold ${STATUS_TONE[status] || "border-transparent bg-v3-lilac text-v3-muted"}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" /> {STATUS[status] || status}
    </span>
  );
}

// M117: status line plus neutral placeholders; no numbers or content before the Core answered.
export function Loading({ label = "Noch keine abschliessenden Inhalte oder Zahlen verfügbar.", sections = ["", "", ""] }) {
  return (
    <div data-testid="member-loading">
      <div role="status" className="mb-[17px] rounded-[12px] bg-v3-lilac px-[20px] pb-[17px] pt-[13px]">
        <p className="flex items-center gap-[12px] text-[16px] font-bold leading-[22px] text-v3-ink"><Info className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />Aktualisierung läuft</p>
        <p className="mt-[9px] text-[14px] leading-[20px] text-v3-muted" data-testid="member-loading-text">{label}</p>
      </div>
      <div className="space-y-[13px]" aria-hidden="true">
        {sections.map((t, i) => (
          <div key={i} className="min-h-[92px] rounded-[10px] border border-v3-line bg-white px-[17px] pb-[19px] pt-[15px]" data-testid="member-loading-card">
            {t && <p className="text-[15px] font-bold leading-[21px] text-v3-ink">{t}</p>}
            <span className={`block h-[9px] w-[67%] animate-pulse rounded-full bg-v3-lilac motion-reduce:animate-none ${t ? "mt-[16px]" : "mt-[10px]"}`} />
            <span className="mt-[12px] block h-[6px] w-[40%] rounded-full bg-v3-lilac/70" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, text, children, testid = "member-empty-state" }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[12px] border border-v3-line bg-white px-6 py-12 text-center" data-testid={testid}>
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-v3-lilac text-v3-magenta"><Icon className="h-6 w-6" aria-hidden="true" /></span>
      <p className="text-[17px] font-bold text-v3-ink">{title}</p>
      {text && <p className="max-w-md text-[14px] text-v3-muted">{text}</p>}
      {children && <div className="mt-1 flex flex-wrap justify-center gap-2">{children}</div>}
    </div>
  );
}

export function Unavailable({ children, testid = "member-unavailable", id }) {
  return (
    <div role="note" id={id} data-testid={testid} className="flex gap-3 rounded-[12px] border border-v3-line bg-v3-sand p-4 text-v3-amber">
      <Info className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 space-y-1 text-sm">
        <p className="font-semibold">Noch nicht verfügbar<span className="sr-only">. </span></p>
        <p>{children || "Diese Funktion wird gerade mit dem gemeinsamen VISIBILIS-System eingerichtet. Hier wird noch nichts gespeichert oder gesendet."}</p>
      </div>
    </div>
  );
}

export function PrivacyNote({ children, testid }) {
  return (
    <p className="flex gap-2 rounded-[12px] bg-v3-lilac p-4 text-[14px] text-v3-ink" data-testid={testid}>
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-v3-magenta" aria-hidden="true" /> <span>{children}</span>
    </p>
  );
}

export function Avatar({ name, color, size = "h-11 w-11 text-sm" }) {
  const initials = (name || "").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span aria-hidden="true" className={`flex shrink-0 items-center justify-center rounded-full font-bold text-white ${size}`} style={{ background: color || "#D40A57" }}>{initials}</span>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, description, children, confirmLabel, cancelLabel = "Abbrechen", onConfirm, busy, testid }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testid}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={testid && `${testid}-cancel`}>{cancelLabel}</AlertDialogCancel>
          <Button onClick={onConfirm} disabled={busy} data-testid={testid && `${testid}-confirm`} className="gap-2 bg-v3-magenta font-bold hover:bg-brand-magenta-dark">
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ErrorNote({ children, testid = "member-inline-error" }) {
  return <p role="alert" data-testid={testid} className="rounded-[12px] border border-red-700/30 bg-red-50 p-3 text-[14px] font-medium text-red-900">{children}</p>;
}

export function CardLink({ to, icon: Icon, title, text, testid }) {
  return (
    <Link to={to} data-testid={testid} className="group flex items-start gap-3 rounded-[12px] border border-v3-line bg-white p-4 transition-shadow hover:shadow-md motion-reduce:transition-none">
      {Icon && <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-v3-blush text-v3-magenta"><Icon className="h-6 w-6" aria-hidden="true" /></span>}
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-bold text-v3-ink group-hover:underline">{title}</span>
        {text && <span className="mt-0.5 block text-[14px] text-v3-muted">{text}</span>}
      </span>
    </Link>
  );
}

export const btnPrimary = "min-h-12 gap-2 rounded-[10px] bg-v3-magenta px-5 text-[15.5px] font-bold text-white hover:bg-brand-magenta-dark";
export const btnOutline = "min-h-12 gap-2 rounded-[10px] border-v3-line bg-white px-5 text-[15.5px] font-bold text-v3-ink hover:bg-v3-lilac";
